<template>
  <div class="pathways-mapping pathways-mapping-template-editor mx-2">
    <h1>{{ local_template && local_template.destination_integration_id ? 'Update' : 'Add' }} Pathway Mapping Template</h1>
    
    <div class="card">
        <div class="card-body">
            <div class="row px-2">
                <div class="col-lg-4 col-12 mb-2">
                    <label>Destination Integration</label>
                    <accordion-select 
                        :modelValue="getSelectedIntegration()"
                        @update:modelValue="changeDestination"
                        :options="formattedIntegrations"
                        :class="{'is-invalid' : errors.destination_integration != ''}"
                        :if_allow_edit="true"
                        name="destination_integration">
                    </accordion-select>
                    <div class="text-danger invalid-feedback" v-if="errors.destination_integration != ''">
                        {{errors.destination_integration}}
                    </div>
                </div>

                <div class="col-lg-4 col-md-6 col-12 mb-2">
                    <label>Source</label>
                    <select class="form-select" :class="{'is-invalid' : errors.source != ''}" v-model="local_template.source"  @change="changeSource()" aria-label="Source">
                        <option v-for="source in sources" :value="source">{{propercase(source)}}</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.source != ''">
                        {{errors.source}}
                    </div>
                </div>

                <div class="col-lg-4 col-md-6 col-12 mb-2" v-if="local_template.source == 'data_blend'">
                    <label>Source Subtype</label>
                    <select class="form-select" :class="{'is-invalid' : errors.source_subtype != ''}" v-model="local_template.source_subtype" 
                    @change="changeSourceSubtype()" :disabled="!local_template.source" aria-label="Source Subtype">
                        <option v-for="source_subtype in datablendSubtypes" :value="source_subtype">{{propercase(source_subtype)}}</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.source_subtype != ''">
                        {{errors.source_subtype}}
                    </div>
                </div>

                <div class="col-lg-4 col-md-6 col-12 mb-2" v-if="local_template.source == 'data_set'">
                    <label>Source Subtype</label>
                    <select class="form-select" :class="{'is-invalid' : errors.source_subtype != ''}" v-model="local_template.source_subtype" 
                    @change="changeSourceSubtype()" :disabled="!local_template.source" aria-label="Source Subtype">
                        <option v-for="source_subtype in datasetSubtypes" :value="source_subtype">{{propercase(source_subtype)}}</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.source_subtype != ''">
                        {{errors.source_subtype}}
                    </div>
                </div> 
            </div>
        </div>
    </div>

    <div class="card">
        <div class="card-body">
            <div class="row px-2">
                <div class="col-12 mb-2">
                    <label>Sample by Client</label>
                    <accordion-select 
                        v-model="local_census_connection" 
                        @update:modelValue="changeConnection"
                        :options="local_connections"
                        :class="{'is-invalid' : errors.sample_by_client != ''}"
                        :disabled="!local_template.destination_integration_id"
                        :if_allow_edit="true">
                    </accordion-select>
                    <div class="text-danger invalid-feedback" v-if="errors.sample_by_client != ''">
                        {{errors.sample_by_client}}
                    </div>
                </div>
                <div class="col-md-6 col-12 mb-2">
                    <label>Destination Object</label>
                    <select class="form-select"  v-model="local_template.destination_object" @change="changeDestinationObject()" :class="{'is-invalid' : errors.destination_object != ''}"
                    :disabled="!local_template.destination_integration_id || !local_census_connection" aria-label="Destination Object">
                        <option v-for="obj in destination_objects" :value="obj.full_name">{{obj.label}}</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.destination_object != ''">
                        {{errors.destination_object}}
                    </div>

                    <div class="form-check mt-2">
                        <input class="form-check-input" type="checkbox" v-model="local_template.if_save_dest_object" id="if_save_dest_object" :disabled="!local_template.destination_integration_id || !local_census_connection" aria-label="If Save Dest Object">
                        <label class="form-check-label" for="if_save_dest_object">
                            Save the value of the Destination Object? (Don't do this if it is client-specific)
                        </label>
                    </div>
                </div>
                <div v-if="operations && operations.length > 1" class="col-md-6 col-12 mt-2 mt-md-0">
                    <label>Method of Sync</label>
                    <select class="form-select" v-model="local_template.operation"
                        :class="{'is-invalid' : errors.operation != ''}"
                        :disabled="!local_template.destination_integration_id || !local_census_connection"  aria-label="Method of Sync">
                        <option v-for="ops in operations" :value="ops" >{{getOperationExplanation(ops)}}</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.operation != ''">
                        {{errors.operation}}
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="card">
        <div class="card-body">
            <div v-if="!local_census_connection" class="text-center py-4">
                <h4>Please select a client to continue</h4>
            </div>
            <div v-else-if="!local_template.destination_object || !local_template.operation" class="text-center py-4">
                <h4>Please select a destination object and operation to continue</h4>
            </div>
            <div v-else-if="loading" class="text-center py-4">
                <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading source columns...</p>
            </div>
            <PathwaysColumnMapping v-else
                :key="'pathways-' + (source_columns ? source_columns.length : 0)"
                v-model="local_columns"
                :destination="local_census_connection"
                :dest_columns="selectedDestinationObject ? selectedDestinationObject.fields : []"
                :source="local_template"
                :source_columns="source_columns"
                :if_on_admin_screen="true"
                :if_allow_edit="true"
                ref="column_mapping"
            />
            
            <button v-if="canAddColumnMapping()" class="btn btn-secondary mt-3" @click="addColumnMapping()" :disabled="busy">
                <i class="fas fa-plus"></i> Add Column Mapping
            </button>
            
            <div class="alert alert-danger mb-3" v-if="errors.form">
                There was an issue saving this pathway mapping template. Check the fields above to get more information.
            </div>

            <div class="mt-3">
                <button type="submit" class="btn btn-primary" @click="save()" :disabled="busy">
                    <span v-if="busy">
                        <div class="spinner-border spinner-border-sm me-2" role="status">
                            <span class="visually-hidden">Saving...</span>
                        </div>
                        Saving...
                    </span>
                    <span v-else>
                        <img class="icon me-2" src="/img/icons/avid-icons/save.svg" alt="">Save Template
                    </span>
                </button>
            </div>
        </div>
    </div>
  </div>

</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import permissionsMixin from '../../mixins/permissions';

export default defineComponent({
    mixins: [permissionsMixin],
    name: 'PathwayMappingTemplateEditor',
    props: {
        columns: {
            type: Array
        },
        integrations: {
            type: Array,
            required: true
        },
        source: {
            type: String,
        },
        datablendSubtypes: {
            type: Array,
            required: true
        },
        datasetSubtypes: {
            type: Array,
            required: true
        },
    },
    data() {
        return {
            local_template: {
                destination_integration_id: null,
                source: null,
                source_subtype: null,
                destination_object: null,
                if_save_dest_object: true,
                operation: null,
            },
            operations: [],
            destination_objects: [],
            selectedDestinationObject: null,

            local_connections: [],
            local_connections_original: [],

            local_census_connection: null,
            local_columns: [],
            sources: ['data_blend', 'data_set'],
            source_columns: [],

            errors: {
                destination_integration_id: '',
                source: '',
                source_subtype: '',
                destination_object: '',
                operation: '',
                form: false,
            },
            busy: false,
            loading: false,
        };
    },
    beforeMount() {
        if(this.columns && this.columns.length > 0){
            this.local_template= {
                destination_integration_id: this.columns[0].destination_integration_id,
                source: this.columns[0].source,
                source_subtype: this.columns[0].source_subtype,
                destination_object: this.columns[0].destination_object,
                operation: this.columns[0].operation,
                if_save_dest_object: this.columns[0].destination_object != null ? true : false
            };

            this.getConnections();
            this.local_columns = this.columns;
            this.destination_objects = this.getDestinationObjects();
            
            // Set the selectedDestinationObject if destination_object is already set
            if (this.local_template.destination_object) {
                this.selectedDestinationObject = this.destination_objects.find(obj => obj.full_name === this.local_template.destination_object);
                
                // Auto-populate primary key destination column if there's only one option and it's not set
                this.autopopulatePrimaryKeyDestination();
            }
        }
    },
    computed: {
        formattedIntegrations() {
            if (!this.integrations || !Array.isArray(this.integrations)) {
                return [];
            }
            
            return this.integrations
                .filter(integration => integration.census_connection_type)
                .map(integration => ({
                    value: integration.id,
                    text: integration.display_name
                }));
        },
    },
    methods: {
        getConnections() {
            if (this.local_template.destination_integration_id && this.local_template.source && this.local_template.source_subtype) {
                window.axios.post('/api/admin/connection/filter_connections', {
                    integration_id: this.local_template.destination_integration_id,
                    source: this.local_template.source,
                    source_subtype: this.local_template.source_subtype
                }).then(response => {
                    
                    this.local_connections = response.data.connections;

                    if (this.local_connections && Array.isArray(this.local_connections)) {
                        this.local_connections_original = this.local_connections.map(connection => ({
                            ...connection,
                            value: connection.id,
                            text: connection.client.name
                        }));

                        // If it's an edit (columns exist), automatically pick the first client
                        if (this.columns && this.columns.length > 0 && this.local_connections_original.length > 0) {
                            this.local_census_connection = this.local_connections_original[0];
                            this.getDataSourceId();
                        }
                    } else {
                        this.local_connections_original = [];
                    }
                });
            }
        },
        getOperationExplanation(ops) {
            if(ops == 'mirror')
                return "Mirror - Records will added, updated, and removed to keep the sources in full sync"
            if(ops == 'append')
                return 'Append - New records will be added to the destination but existing will not be updated or removed';
            if(ops == 'upsert')
                return "Update or Create - Update existing destination records when IDs match, otherwise create new ones if they're missing"
            if(ops == 'update')
                return "Update Only - Matching records will be updated but nothing will be added or removed"
            if(ops == 'delete')
                return "Delete - Delete syncs takes the list of provided IDs and deletes them from the destination. Be careful! Providing an incorrect set of IDs can result in data loss!"
            if(ops == 'create' || ops == 'insert')
                return "Create Only - Create a new object if it doesn't exist in the destination"
        },
        changeDestination(selectedIntegration) {
            // Extract the value from the AccordionSelect component if it's an object
            if (selectedIntegration && typeof selectedIntegration === 'object' && selectedIntegration.value !== undefined) {
                this.local_template.destination_integration_id = selectedIntegration.value;
            }
            this.getConnections();
            this.destination_objects = this.getDestinationObjects();
            // Reset destination, sample by client, and operation when integration changes 
            this.local_template.destination_object = null;
            this.local_template.operation = null;
            this.selectedDestinationObject = null;
            this.local_census_connection = null;
        },
        changeDestinationObject() {
            this.local_template.operation = null;

            if (!this.destination_objects || !Array.isArray(this.destination_objects)) {
                this.operations = [];
                return;
            }

            const selectedObject = this.destination_objects.find(obj => obj.full_name === this.local_template.destination_object);
            if (selectedObject) {
                // Store the full object reference for accessing fields
                this.selectedDestinationObject = selectedObject;
                
                if (selectedObject.supported_operations && selectedObject.supported_operations.length > 0) {
                    // Auto-select operation if there's only one
                    this.operations = selectedObject.supported_operations;
                    if (this.operations.length === 1) {
                        this.local_template.operation = this.operations[0];
                        // Auto-populate primary key destination column when operation is auto-selected
                        this.$nextTick(() => {
                            this.autopopulatePrimaryKeyDestination();
                        });
                    }
                }
            } else {
                this.operations = [];
                this.selectedDestinationObject = null;
            }

            // Auto-select if there's only one destination object
            if (this.destination_objects.length === 1 && !this.local_template.destination_object) {
                this.local_template.destination_object = this.destination_objects[0].full_name;
                this.changeDestinationObject();
            }
            
            // Auto-populate primary key destination column if conditions are met
            this.autopopulatePrimaryKeyDestination();
        },
        changeSource() {
            this.local_template.source_subtype = null;
            this.local_census_connection = null;
            this.getConnections();
        },
        changeSourceSubtype() {
            this.local_census_connection = null;
            this.getConnections();
        },
        changeConnection(selectedConnection) {
            // Find the full connection object from local_connections_original
            if (!this.local_connections_original || !Array.isArray(this.local_connections_original)) {
                return;
            }
            
            const fullConnection = this.local_connections_original.find(conn => conn.id === selectedConnection.value);
            if (fullConnection) {
                //console.log(this.local_columns);
                this.local_census_connection = fullConnection;
                this.getDataSourceId();
            }
        },
        getDataSourceId() {
            if (!this.local_census_connection || !this.local_template.source || !this.local_template.source_subtype) {
                this.hasErrors();
                return;
            }
            this.loading = true;
            window.axios.get('/api/admin/connection/' + this.local_template.source + '/' + this.local_template.source_subtype + '/' + this.local_census_connection.client_id + '/' + this.local_census_connection.id)
            .then(response => {
                const data_source_id = response.data.data_source_id;
                this.fetchSourceColumns(data_source_id);
            });
        },
        fetchSourceColumns(data_source_id) {
            if (!this.local_census_connection || !this.local_template.source || !this.local_template.source_subtype) {
                this.hasErrors();
                return;
            }
            
            let data = {
                client: this.local_census_connection.client,
                include_trans: false,
                source: this.local_template.source,
                data_source_id: data_source_id,
            };
            
            window.axios.post('/api/bigquery/get_db_columns', data)
            .then(response => {
                this.source_columns = response.data.columns;
                this.loading = false;
                //console.log(this.local_columns);
            });
        },
        getSelectedIntegration() {
            if (!this.local_template.destination_integration_id) {
                return null;
            }
            
            const integration = this.integrations.find(integration => integration.id === this.local_template.destination_integration_id);
            if (!integration) {
                return null;
            }
            
            if (this.local_connections_original && Array.isArray(this.local_connections_original)) {
                this.local_connections = [...this.local_connections_original.filter(connection => connection.integration_id == this.local_template.destination_integration_id)];
            } else {
                this.local_connections = [];
            }
            
            return {
                value: integration.id,
                text: integration.display_name
            };
        },
        getDestinationObjects() {
            if (!this.local_template.destination_integration_id) {
                return [];
            }
            
            const integration = this.integrations.find(integration => integration.id === this.local_template.destination_integration_id);
            if (!integration || !integration.destination_objects || !Array.isArray(integration.destination_objects)) {
                return [];
            }
            
            return integration.destination_objects;
        },
        addColumnMapping(){
            if (!this.local_columns || !Array.isArray(this.local_columns)) {
                this.local_columns = [];
            }
            
            this.local_columns.push({
                id: -1 * Math.random(),
                destination_integration_id: this.local_template.destination_integration_id,
                source: this.local_template.source,
                source_subtype: this.local_template.source_subtype,
                destination_object: this.local_template.destination_object,
                operation: this.local_template.operation,
                source_column: this.getBlankSourceColumn(),
                destination_column: null,
                is_primary_identifier: false,
            });
        },
        getBlankSourceColumn() {
            return {
                main_category: 'Columns',
                category: null,
                type: 'string',
                value: null,
                text: ""
            };
        },
        canAddColumnMapping() {
            return this.source_columns && 
                    Array.isArray(this.source_columns) &&
                    this.source_columns.length > 0 && 
                    this.local_template.destination_integration_id && 
                    this.local_template.source && 
                    this.local_template.destination_object;

        },
        hasErrors() {
            let is_valid = true;

            if (!this.local_template.destination_integration_id) {
                this.errors.destination_integration_id = 'Destination integration is required';
                is_valid = false;
            }
            
            if (!this.local_template.source) {
                this.errors.source = 'Source is required';
                is_valid = false;
            }
            
            if (!this.local_template.destination_object) {
                this.errors.destination_object = 'Destination object is required';
                is_valid = false;
            }
            
            if (!this.local_template.operation) {
                this.errors.operation = 'Operation is required';
                is_valid = false;
            }

            // Set form error if validation fails
            if (!is_valid) {
                this.errors.form = true;
            } else {
                this.errors.form = false;
            }

            return !is_valid;
        },
        autopopulatePrimaryKeyDestination() {
            if (!this.local_columns || !Array.isArray(this.local_columns) || !this.selectedDestinationObject) {
                return;
            }
            
            // Find the primary key column (usually the first one with is_primary_identifier: true)
            const primaryKeyColumn = this.local_columns.find(column => column.is_primary_identifier);
            
            if (!primaryKeyColumn || primaryKeyColumn.destination_column) {
                // No primary key column found or it already has a destination column
                return;
            }
            
            // Get available primary key destinations based on the operation
            const availablePrimaryKeyDestinations = this.getPrimaryKeyDestinations();
            
            // If there's only one option, auto-populate it
            if (availablePrimaryKeyDestinations.length === 1) {
                primaryKeyColumn.destination_column = availablePrimaryKeyDestinations[0].value;
            }
        },
        getPrimaryKeyDestinations() {
            if (!this.selectedDestinationObject || !this.selectedDestinationObject.fields || !this.local_template.operation) {
                return [];
            }
            
            const operation = this.local_template.operation;
            let keyField = 'can_be_' + operation + '_key';
            
            // Handle special operation mappings
            if (operation === 'mirror') keyField = 'can_be_upsert_key';
            else if (operation === 'delete') keyField = 'can_be_update_key';
            else if (operation === 'create' || operation === 'append') keyField = 'can_be_insert_key';
            
            return this.selectedDestinationObject.fields.filter(field => {
                return field[keyField] === true && field.type !== 'Unmappable';
            }).map(field => ({
                value: field.full_name,
                text: field.label
            }));
        },
        save() {
            if (!this.hasErrors()) {
                this.busy = true;

                // Process the source_column data for each column
                this.local_columns.forEach(column => {
                    column.source_column.type = 'string';
                    
                    if (column.source_column.value && typeof column.source_column.value === 'object') {
                        if(column.source_column.value.category)
                            column.source_column.category = column.source_column.value.category.toLowerCase();
                        column.source_column.text = column.source_column.value.text;
                        column.source_column.value = column.source_column.value.value;
                    }

                    //If the destinatino_column is an object or array, change it to the value
                    if(column.destination_column && typeof column.destination_column === 'object' || typeof column.destination_column === 'array') {
                        column.destination_column = column.destination_column.value;
                    }
                    
                    // Ensure all template-level data is set on each column
                    column.destination_integration_id = this.local_template.destination_integration_id;
                    column.source = this.local_template.source;
                    column.source_subtype = this.local_template.source_subtype;
                    column.operation = this.local_template.operation;
                    
                    // Set destination_object only if if_save_dest_object is true
                    if (this.local_template.if_save_dest_object) {
                        column.destination_object = this.local_template.destination_object;
                    } else {
                        column.destination_object = null;
                    }
                });

                const isEdit = this.columns && this.columns.length > 0;
                
                window.axios.post('/api/admin/pathway_mapping_template/save', this.local_columns)
                .then(response => {
                    if(response.data.success){
                        this.busy = false;
                        Swal.fire({
                            title: "Success!",
                            html: `Pathway mapping template has been ${isEdit ? 'updated' : 'created'} successfully.`,
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            // Redirect to the template list after successful save
                            window.location.href = '/admin/pathway_mapping_templates';
                        });
                    } else {
                        this.busy = false;
                        Swal.fire({
                            title: "Error!",
                            html: response.data.message || "There was an error saving the pathway mapping template.",
                            icon: "error",
                            customClass: {
                                popup: 'swal-error'
                            },
                        });
                    }
                }).catch(error => {
                    this.busy = false;
                    
                    let errorMessage = "There was an error saving the pathway mapping template.";
                    let showEditLink = false;
                    let editUrl = '';
                    
                    if (error.response && error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                            
                            // Check if this is a duplicate template error
                            if (error.response.data.duplicate_template) {
                                showEditLink = true;
                                editUrl = error.response.data.duplicate_template.edit_url;
                                errorMessage += '<br><br>Would you like to edit the existing template instead?';
                            }
                        } else if (error.response.data.errors) {
                            // Handle validation errors
                            const validationErrors = Object.values(error.response.data.errors).flat();
                            errorMessage = validationErrors.join('<br>');
                        }
                    }
                    
                    Swal.fire({
                        title: "Error!",
                        html: errorMessage,
                        icon: "error",
                        showCancelButton: showEditLink,
                        confirmButtonText: showEditLink ? "Edit Existing Template" : "OK",
                        cancelButtonText: showEditLink ? "Cancel" : null,
                        customClass: {
                            popup: 'swal-error'
                        },
                    }).then((result) => {
                        if (result.isConfirmed && showEditLink) {
                            window.location.href = editUrl;
                        }
                    });
                    
                    console.error('Save error:', error);
                });
            }
        },
    }
});
</script>
